import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import {
  pedagog,
  student,
} from '../../../components/common/common-functions/common-functions';
import { getCommonUrl } from '../../../components/common/common-functions/commonApiUrl/get-common-url';
import { getLegalClinicUrl } from '../../../components/common/common-functions/commonApiUrl/get-klinike-ligjore-url';
import getConfig from '../../../helpers/config';
import { IdEntity } from '../../../interfaces/common-interfaces';
import { UpdateKlasaStudent } from '../../../interfaces/klasa-interface';
import { LendaTematProps } from '../../../interfaces/lenda-akademike.interface';
import {
  AddLendeGjyqeProps,
  LegalClinicMockTrialParams,
  LendeGjyqParams,
  LendeGjyqResponse,
  LendeGjyqTema,
} from '../../../interfaces/lende-gjyqe.interface';
import { PaginationReturn } from '../../../interfaces/pagination';
import { getPrepareHeaders } from '../get-prepare-headers';

const { test } = getConfig();

const getMockTrialApi = (params: LendeGjyqParams, url = 'lende-gjyq') => {
  return getCommonUrl(params, `/${url}`, 'dataFillimit', {
    field: 'vitiAkademikAktiv',
    order: params.sort || 'asc',
  });
};

export type MockTrialType = PaginationReturn<LendeGjyqResponse>;

export const lendeGjyqApi = createApi({
  reducerPath: 'lendeGjyqApi',
  baseQuery: fetchBaseQuery({
    baseUrl: test.baseUrl + `/`,
    prepareHeaders: getPrepareHeaders,
  }),
  tagTypes: ['allMockTrials', 'allLegalClinics'],
  endpoints: builder => ({
    getAllLendeGjyq: builder.query<MockTrialType, LendeGjyqParams>({
      query: getMockTrialApi,
      providesTags: ['allMockTrials'],
    }),
    getLendeGjyqPedagog: builder.query<MockTrialType, LendeGjyqParams>({
      query: args => getMockTrialApi(args, 'lende-gjyq-klinika/per-pedagog'),
      providesTags: ['allMockTrials'],
    }),
    getLendeGjyqStudent: builder.query<MockTrialType, LendeGjyqParams>({
      query: args => getMockTrialApi(args, 'lende-gjyq-klinika/per-student'),
      providesTags: ['allMockTrials'],
    }),
    getAllLendeGjyqPedagog: builder.query<MockTrialType, LendeGjyqParams>({
      query: args =>
        getMockTrialApi(args, 'lende-gjyq-klinika/per-pedagog/simplified'),
      providesTags: ['allMockTrials'],
    }),
    getSingleLendeGjyqTema: builder.query<LendeGjyqTema, IdEntity>({
      query: ({ id, role }) => ({
        url: `/lende-gjyq-klinika/${id}/lende-tema${role === pedagog ? '/per-pedagog' : role === student ? '/per-student' : ''}`,
      }),
      providesTags: ['allMockTrials'],
    }),
    getSimplifiedLegalClinicMockTrial: builder.query<
      MockTrialType,
      LegalClinicMockTrialParams
    >({
      query: args => ({
        url: getCommonUrl(args, 'lende-gjyq-klinika/simplified'),
      }),
      providesTags: ['allMockTrials', 'allLegalClinics'],
    }),
    getTopicsPerGjyqStudent: builder.query<LendeGjyqTema, number>({
      query: id => ({
        url: `/lenda-tema/gjyq-klinike/${id}/per-student`,
      }),
      providesTags: ['allMockTrials'],
    }),

    getLendeGjyqByGroup: builder.query<MockTrialType, number>({
      query: id => ({
        url: `/lende-gjyq/sipas-grup/${id}`,
      }),
      providesTags: ['allMockTrials'],
    }),
    addMockTrial: builder.mutation<number, AddLendeGjyqeProps>({
      query: body => ({
        url: '/lende-gjyq',
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['allMockTrials'],
    }),
    updateMockTrial: builder.mutation<
      number,
      UpdateKlasaStudent<AddLendeGjyqeProps>
    >({
      query: ({ body, id }) => ({
        url: `/lende-gjyq/${id}`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: ['allMockTrials'],
    }),
    deleteMockTrial: builder.mutation<number, number>({
      query: id => ({
        url: `/lende-gjyq-klinika/${id}`,
        method: 'DELETE',
        responseHandler: response => response.text(),
      }),
      invalidatesTags: ['allMockTrials', 'allLegalClinics'],
    }),
    addTemaMockTrial: builder.mutation<
      number,
      UpdateKlasaStudent<LendaTematProps>
    >({
      query: ({ id, body }) => ({
        url: `/lenda-tema/gjyq-klinike/${id}`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['allMockTrials'],
    }),
    updateTemaMockTrial: builder.mutation<
      number,
      UpdateKlasaStudent<LendaTematProps>
    >({
      query: ({ id, body }) => ({
        url: `/lenda-tema/gjyq-klinike/${id}`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: ['allMockTrials'],
    }),
    deleteTemaMockTrial: builder.mutation<number, number>({
      query: id => ({
        url: `/lenda-tema/gjyq-klinike/${id}`,
        method: 'DELETE',
        responseHandler: response => response.text(),
      }),
      invalidatesTags: ['allMockTrials'],
    }),
    closeEvaluations: builder.mutation<number, number>({
      query: id => ({
        url: `/lende-gjyq/${id}/perfundo-vleresim`,
        method: 'PUT',
      }),
      invalidatesTags: ['allMockTrials'],
    }),
    getAllLegalClinics: builder.query<MockTrialType, LendeGjyqParams>({
      query: getLegalClinicUrl,
      providesTags: ['allLegalClinics'],
    }),
    getLegalClinicByGroup: builder.query<MockTrialType, number>({
      query: id => ({
        url: `/lende-klinike/sipas-grup/${id}`,
      }),
      providesTags: ['allLegalClinics'],
    }),
    addLegalClinic: builder.mutation<number, AddLendeGjyqeProps>({
      query: body => ({
        url: '/lende-klinike',
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['allLegalClinics'],
    }),
    updateLegalClinic: builder.mutation<
      number,
      UpdateKlasaStudent<AddLendeGjyqeProps>
    >({
      query: ({ body, id }) => ({
        url: `/lende-klinike/${id}`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: ['allLegalClinics'],
    }),
  }),
});

export const {
  useGetAllLendeGjyqQuery,
  useLazyGetAllLendeGjyqQuery,
  useGetLendeGjyqPedagogQuery,
  useLazyGetLendeGjyqPedagogQuery,
  useLazyGetAllLegalClinicsQuery,
  useGetLendeGjyqStudentQuery,
  useGetTopicsPerGjyqStudentQuery,
  useLazyGetAllLendeGjyqPedagogQuery,
  useAddMockTrialMutation,
  useUpdateMockTrialMutation,
  useDeleteMockTrialMutation,
  useGetLendeGjyqByGroupQuery,
  useAddTemaMockTrialMutation,
  useGetSingleLendeGjyqTemaQuery,
  useLazyGetSingleLendeGjyqTemaQuery,
  useDeleteTemaMockTrialMutation,
  useUpdateTemaMockTrialMutation,
  useCloseEvaluationsMutation,
  useGetAllLegalClinicsQuery,
  useAddLegalClinicMutation,
  useUpdateLegalClinicMutation,
  useGetLegalClinicByGroupQuery,
  useLazyGetSimplifiedLegalClinicMockTrialQuery,
} = lendeGjyqApi;
